// RelatorioDfesPorEmitir.jsx

import { useState, useEffect } from "react";
import { useHttpClient } from "../../../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../../../shared/context/LoadingContext";
import TabelaDfesPorEmitir from "./TabelaDfesPorEmitir";
import * as XLSX from "xlsx";
import "./RelatorioDfesPorEmitir.scss";

function RelatorioDfesPorEmitir({
  dataFiltro,
  dadosClinica,
  auth,
  acao,
  atualizacoes,
  unidadeSelecionada = "",
}) {
  const { sendRequest } = useHttpClient();
  const { startLoading, stopLoading } = useLoading();

  const [dados, setDados] = useState({
    contasCorrentes: [],
    entradas: [],
  });

  useEffect(() => {
    if (acao === "gerar") {
      buscarDfesPorEmitir();
    } else if (acao === "exportar") {
      exportarRelatorio();
    }
  }, [atualizacoes]);

  const buscarDfesPorEmitir = async () => {
    startLoading();
    try {
      // 1. Buscar Contas Correntes
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/documentoeletronico/getDfesPorEmitir/${dadosClinica._id}`,
        "POST",
        JSON.stringify({
          startDate: dataFiltro.dataInicial,
          endDate: dataFiltro.dataFinal,
          // Vazio = consolidado da empresa. Registos ainda sem unidade
          // continuam a aparecer em qualquer unidade escolhida — é uma lista de
          // trabalho pendente, e esconder o que falta fazer seria pior.
          ...(unidadeSelecionada && { estabelecimento: unidadeSelecionada }),
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      setDados({
        contasCorrentes: responseData.contasCorrentes || [],
        entradas: responseData.entradas || [],
      });
    } catch (err) {
      console.error("Erro ao buscar DFEs por emitir:", err);
    } finally {
      stopLoading();
    }
  };

  const exportarRelatorio = async () => {
    try {
      startLoading();

      // 1. Buscar dados (mesmo endpoint do gerar)
      //
      // Antes eram dois pedidos a /api/contascorrentes/getDfesPorEmitir com um
      // `tipo` — rota que não existe no backend, portanto a exportação dava 404
      // e nunca produziu ficheiro. O endpoint certo devolve as duas listas de
      // uma só vez.
      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/documentoeletronico/getDfesPorEmitir/${dadosClinica._id}`,
        "POST",
        JSON.stringify({
          startDate: dataFiltro.dataInicial,
          endDate: dataFiltro.dataFinal,
          ...(unidadeSelecionada && { estabelecimento: unidadeSelecionada }),
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      const responseCC = { contasCorrentes: resposta.contasCorrentes || [] };
      const responseEntradas = { entradas: resposta.entradas || [] };

      // 2. Preparar dados para Excel
      const contasCorrentes = prepararDadosContasCorrentes(
        responseCC.contasCorrentes || [],
      );
      const entradas = prepararDadosEntradas(responseEntradas.entradas || []);

      // 3. Criar workbook
      const wb = XLSX.utils.book_new();

      // 4. Adicionar sheets
      if (contasCorrentes.length > 0) {
        const wsCC = XLSX.utils.json_to_sheet(contasCorrentes);
        ajustarLarguraColunas(wsCC, contasCorrentes);
        XLSX.utils.book_append_sheet(wb, wsCC, "Contas Correntes");
      }

      if (entradas.length > 0) {
        const wsEntradas = XLSX.utils.json_to_sheet(entradas);
        ajustarLarguraColunas(wsEntradas, entradas);
        XLSX.utils.book_append_sheet(wb, wsEntradas, "Entradas");
      }

      // 5. Gerar e baixar arquivo
      const nomeArquivo = gerarNomeArquivo(
        dataFiltro.dataInicial,
        dataFiltro.dataFinal,
      );
      XLSX.writeFile(wb, nomeArquivo);

      stopLoading();
    } catch (err) {
      console.error("Erro ao exportar relatório:", err);
      stopLoading();
      // Adicionar toast de erro aqui se tiver
    }
  };

  // Função auxiliar: Preparar dados de Contas Correntes
  const prepararDadosContasCorrentes = (contasCorrentes) => {
    const dados = [];

    contasCorrentes.forEach((contaCorrente) => {
      const contas = Array.isArray(contaCorrente.contas)
        ? contaCorrente.contas
        : [contaCorrente.contas];

      contas.forEach((conta) => {
        const parcelas = Array.isArray(conta.parcelas)
          ? conta.parcelas
          : [conta.parcelas];

        parcelas.forEach((parcela, idx) => {
          dados.push({
            "Tipo DFE": "FRE/TVE",
            Cliente: contaCorrente.cliente?.nome || "N/A",
            "NIF Cliente": contaCorrente.cliente?.nif || "",
            "Código Orçamento": conta.orcamento?.codigo || "N/A",
            Benefício: conta.orcamento?.beneficio || "",
            "Nº Parcela": parcela.nrParcela || idx + 1,
            "Valor (CVE)": parcela.quantiaPaga.toLocaleString("pt-Br") || 0,
            "Data Pagamento": parcela.dataPagamento
              ? new Date(parcela.dataPagamento).toLocaleDateString("pt-PT")
              : "N/A",
            "Método Pagamento": parcela.metodoPagamento || "N/A",
            "Total Conta (CVE)": conta.total.toLocaleString("pt-Br") || 0,
          });
        });
      });
    });

    return dados;
  };

  // Função auxiliar: Preparar dados de Entradas
  const prepararDadosEntradas = (entradas) => {
    return entradas.map((entrada) => {
      // Determinar tipo de DFE
      let tipoDfe = "N/A";
      if (entrada.tipo === "reembolso") {
        tipoDfe = "FRE/TVE";
      } else if (entrada.tipo === "clienteFornecedor") {
        tipoDfe = entrada.documentoEletronico ? "RCE" : "FTE";
      }

      // Determinar nome (cliente ou fornecedor)
      let nome = "N/A";
      if (entrada.tipo === "reembolso" && entrada.cliente) {
        nome = entrada.cliente.nome;
      } else if (entrada.clienteFornecedor) {
        nome = entrada.clienteFornecedor.nome;
      }

      return {
        "Tipo DFE": tipoDfe,
        "Tipo Entrada":
          entrada.tipo === "reembolso" ? "Cliente" : "Cliente/Fornecedor",
        "Cliente/Fornecedor": nome,
        Descrição: entrada.descricao || "N/A",
        "Valor (CVE)": entrada.valor.toLocaleString("pt-Br") || 0,
        "Data Pagamento": entrada.dataPagamento
          ? new Date(entrada.dataPagamento).toLocaleDateString("pt-PT")
          : "-",
        "Data Limite": entrada.dataLimitePagamento
          ? new Date(entrada.dataLimitePagamento).toLocaleDateString("pt-PT")
          : "-",
        "Método Pagamento": entrada.metodoPagamento || "N/A",
        Observações: entrada.observacoes || "",
      };
    });
  };

  // Função auxiliar: Ajustar largura das colunas
  const ajustarLarguraColunas = (ws, dados) => {
    if (!dados || dados.length === 0) return;

    const colunas = Object.keys(dados[0]);
    const larguras = colunas.map((col) => {
      const maxLength = Math.max(
        col.length,
        ...dados.map((row) => {
          const val = row[col];
          return val ? String(val).length : 0;
        }),
      );
      return { wch: Math.min(maxLength + 2, 50) }; // Max 50 caracteres
    });

    ws["!cols"] = larguras;
  };

  // Função auxiliar: Gerar nome do arquivo
  const gerarNomeArquivo = (dataInicial, dataFinal) => {
    const agora = new Date();
    const timestamp = agora.toISOString().split("T")[0]; // YYYY-MM-DD

    if (dataInicial && dataFinal) {
      const inicio = new Date(dataInicial)
        .toLocaleDateString("pt-PT")
        .replace(/\//g, "-");
      const fim = new Date(dataFinal)
        .toLocaleDateString("pt-PT")
        .replace(/\//g, "-");
      return `DFEs_Por_Emitir_${inicio}_a_${fim}_${timestamp}.xlsx`;
    }

    return `DFEs_Por_Emitir_Todos_${timestamp}.xlsx`;
  };

  return (
    <div className="relatorio-dfes-por-emitir">
      <h3>Documentos Fiscais Eletrónicos Por Emitir</h3>

      <div className="resumo">
        <div className="resumo-item">
          <span>Contas Correntes:</span>
          <strong>{dados.contasCorrentes.length}</strong>
        </div>
        <div className="resumo-item">
          <span>Entradas:</span>
          <strong>{dados.entradas.length}</strong>
        </div>
      </div>

      <TabelaDfesPorEmitir dados={dados} />
    </div>
  );
}

export default RelatorioDfesPorEmitir;
